import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { getApiErrorMessage } from '../../utils/apiError';

// Initial state
const initialState = {
  roles: [],
  permissions: [],
  currentRole: null,
  isLoading: false,
  error: null,
};

// Async thunks for roles
export const fetchRoles = createAsyncThunk(
  'roles/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/roles');
      const roles = response.data.data.filter(role =>
        role.id && Number.isInteger(Number(role.id)) && Number(role.id) > 0
      );
      return roles;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch roles'));
    }
  }
);

export const fetchPermissions = createAsyncThunk(
  'roles/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/permissions`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch permissions'));
    }
  }
);

export const createRole = createAsyncThunk(
  'roles/createRole',
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await api.post('/roles', roleData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create role'));
    }
  }
);

export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async ({ id, roleData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/roles/${id}`, roleData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update role'));
    }
  }
);

export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async (roleId, { rejectWithValue }) => {
    try {
      await api.delete(`/roles/${roleId}`);
      return roleId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete role'));
    }
  }
);

export const assignPermissionsToRole = createAsyncThunk(
  'roles/assignPermissionsToRole',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/roles/${roleId}/assign-permissions`, {permissions});
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to assign permissions'));
    }
  }
);

// Roles slice
const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRole: (state, action) => {
      state.currentRole = action.payload;
    },
    clearCurrentRole: (state) => {
      state.currentRole = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch roles
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create role
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles.push(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update role
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.roles.findIndex(role => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.currentRole && state.currentRole.id === action.payload.id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete role
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = state.roles.filter(role => role.id !== action.payload);
        if (state.currentRole && state.currentRole.id === action.payload) {
          state.currentRole = null;
        }
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Assign permissions to role
      .addCase(assignPermissionsToRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignPermissionsToRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.roles.findIndex(role => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.currentRole && state.currentRole.id === action.payload.id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(assignPermissionsToRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentRole, clearCurrentRole } = rolesSlice.actions;
export default rolesSlice.reducer;